// Roles & Permissions Controller
// This file manages all role and permission-related operations

class RolesPermissionsController {
    constructor() {
      this.ROLES_KEY = 'system_roles';
      this.PERMISSIONS_KEY = 'system_permissions';
      this.USER_ROLES_KEY = 'user_roles_mapping';
      this.initializeDefaultData();
    }
  
    /**
     * Initialize default roles and permissions
     */
    initializeDefaultData() {
      // Initialize default roles if not exists
      if (!localStorage.getItem(this.ROLES_KEY)) {
        const defaultRoles = [
          {
            id: 'super_admin',
            name: 'Super Admin',
            description: 'Full system access with all permissions',
            permissions: ['all'],
            userCount: 0,
            isSystem: true,
            color: '#f59e0b',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          },
          {
            id: 'admin',
            name: 'Admin',
            description: 'Administrative access with subscription-based sections',
            permissions: ['dashboard', 'users', 'settings', 'reports', 'roles'],
            userCount: 0,
            isSystem: true,
            color: '#3b82f6',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          },
          {
            id: 'manager',
            name: 'Manager',
            description: 'Team management and reporting access',
            permissions: ['dashboard', 'reports', 'team_management', 'projects'],
            userCount: 0,
            isSystem: false,
            color: '#8b5cf6',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          },
          {
            id: 'employee',
            name: 'Employee',
            description: 'Basic employee access based on assigned permissions',
            permissions: ['dashboard', 'profile'],
            userCount: 0,
            isSystem: false,
            color: '#10b981',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          }
        ];
        localStorage.setItem(this.ROLES_KEY, JSON.stringify(defaultRoles));
      }
  
      // Initialize default permissions if not exists
      if (!localStorage.getItem(this.PERMISSIONS_KEY)) {
        const defaultPermissions = [
          // Core Permissions
          { id: 'dashboard', name: 'Dashboard', category: 'Core', description: 'View dashboard' },
          { id: 'profile', name: 'Profile', category: 'Core', description: 'View and edit own profile' },
          
          // Admin Permissions
          { id: 'users', name: 'User Management', category: 'Admin', description: 'Manage users' },
          { id: 'roles', name: 'Roles & Permissions', category: 'Admin', description: 'Manage roles and permissions' },
          { id: 'settings', name: 'Settings', category: 'Admin', description: 'System settings' },
          
          // Section Permissions
          { id: 'hospital', name: 'Hospital Management', category: 'Healthcare', description: 'Full hospital management access' },
          { id: 'crm', name: 'CRM', category: 'Sales', description: 'Customer relationship management' },
          { id: 'hrms', name: 'HRMS', category: 'HR', description: 'Human resource management' },
          { id: 'projects', name: 'Project Management', category: 'Operations', description: 'Project and task management' },
          { id: 'inventory', name: 'Inventory', category: 'Operations', description: 'Inventory management' },
          { id: 'sales', name: 'Sales', category: 'Sales', description: 'Sales management' },
          { id: 'orders', name: 'Order Management', category: 'Operations', description: 'Order processing' },
          { id: 'reports', name: 'Reports', category: 'Analytics', description: 'View and generate reports' },
          { id: 'communication', name: 'Communication', category: 'Collaboration', description: 'Internal communication' },
          { id: 'wallet', name: 'Wallet & Payments', category: 'Finance', description: 'Payment management' },
          
          // Management Permissions
          { id: 'team_management', name: 'Team Management', category: 'Management', description: 'Manage team members' },
          { id: 'approvals', name: 'Approvals', category: 'Management', description: 'Approve requests' },
          { id: 'performance', name: 'Performance', category: 'Management', description: 'Performance reviews' }
        ];
        localStorage.setItem(this.PERMISSIONS_KEY, JSON.stringify(defaultPermissions));
      }
    }
  
    /**
     * Get all roles
     * @returns {Array} Array of role objects
     */
    getAllRoles() {
      try {
        const roles = localStorage.getItem(this.ROLES_KEY);
        return roles ? JSON.parse(roles) : [];
      } catch (error) {
        console.error('Error getting roles:', error);
        return [];
      }
    }
  
    /**
     * Get role by ID
     * @param {string} roleId - Role ID
     * @returns {Object|null} Role object or null
     */
    getRoleById(roleId) {
      try {
        const roles = this.getAllRoles();
        return roles.find(role => role.id === roleId) || null;
      } catch (error) {
        console.error('Error getting role:', error);
        return null;
      }
    }
  
    /**
     * Create new role
     * @param {Object} roleData - Role data
     * @returns {Object|null} Created role or null
     */
    createRole(roleData) {
      try {
        const roles = this.getAllRoles();
        
        // Generate ID from name
        const roleId = roleData.name.toLowerCase().replace(/\s+/g, '_');
        
        // Check if role already exists
        if (roles.find(r => r.id === roleId)) {
          throw new Error('Role with this name already exists');
        }
  
        const newRole = {
          id: roleId,
          name: roleData.name,
          description: roleData.description || '',
          permissions: roleData.permissions || [],
          userCount: 0,
          isSystem: false,
          color: roleData.color || '#64748b',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };
  
        roles.push(newRole);
        localStorage.setItem(this.ROLES_KEY, JSON.stringify(roles));
        
        return newRole;
      } catch (error) {
        console.error('Error creating role:', error);
        return null;
      }
    }
  
    /**
     * Update role
     * @param {string} roleId - Role ID
     * @param {Object} updates - Role updates
     * @returns {boolean} Success status
     */
    updateRole(roleId, updates) {
      try {
        const roles = this.getAllRoles();
        const roleIndex = roles.findIndex(r => r.id === roleId);
        
        if (roleIndex === -1) {
          throw new Error('Role not found');
        }
  
        // Prevent updating system roles (except permissions)
        if (roles[roleIndex].isSystem && !updates.permissions) {
          throw new Error('Cannot modify system role');
        }
  
        roles[roleIndex] = {
          ...roles[roleIndex],
          ...updates,
          updatedAt: new Date().toISOString()
        };
  
        localStorage.setItem(this.ROLES_KEY, JSON.stringify(roles));
        return true;
      } catch (error) {
        console.error('Error updating role:', error);
        return false;
      }
    }
  
    /**
     * Delete role
     * @param {string} roleId - Role ID
     * @returns {boolean} Success status
     */
    deleteRole(roleId) {
      try {
        const roles = this.getAllRoles();
        const role = roles.find(r => r.id === roleId);
        
        if (!role) {
          throw new Error('Role not found');
        }
  
        if (role.isSystem) {
          throw new Error('Cannot delete system role');
        }
  
        if (role.userCount > 0) {
          throw new Error('Cannot delete role with assigned users');
        }
  
        const updatedRoles = roles.filter(r => r.id !== roleId);
        localStorage.setItem(this.ROLES_KEY, JSON.stringify(updatedRoles));
        
        return true;
      } catch (error) {
        console.error('Error deleting role:', error);
        return false;
      }
    }
  
    /**
     * Duplicate role
     * @param {string} roleId - Role ID to duplicate
     * @returns {Object|null} Duplicated role or null
     */
    duplicateRole(roleId) {
      try {
        const role = this.getRoleById(roleId);
        
        if (!role) {
          throw new Error('Role not found');
        }
  
        const duplicatedRole = {
          ...role,
          id: `${role.id}_copy_${Date.now()}`,
          name: `${role.name} (Copy)`,
          userCount: 0,
          isSystem: false,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };
  
        const roles = this.getAllRoles();
        roles.push(duplicatedRole);
        localStorage.setItem(this.ROLES_KEY, JSON.stringify(roles));
        
        return duplicatedRole;
      } catch (error) {
        console.error('Error duplicating role:', error);
        return null;
      }
    }
  
    /**
     * Get all permissions
     * @returns {Array} Array of permission objects
     */
    getAllPermissions() {
      try {
        const permissions = localStorage.getItem(this.PERMISSIONS_KEY);
        return permissions ? JSON.parse(permissions) : [];
      } catch (error) {
        console.error('Error getting permissions:', error);
        return [];
      }
    }
  
    /**
     * Get permissions grouped by category
     * @returns {Object} Grouped permissions
     */
    getGroupedPermissions() {
      try {
        const permissions = this.getAllPermissions();
        return permissions.reduce((acc, perm) => {
          if (!acc[perm.category]) {
            acc[perm.category] = [];
          }
          acc[perm.category].push(perm);
          return acc;
        }, {});
      } catch (error) {
        console.error('Error grouping permissions:', error);
        return {};
      }
    }
  
    /**
     * Assign role to user
     * @param {string} userId - User ID
     * @param {string} roleId - Role ID
     * @returns {boolean} Success status
     */
    assignRoleToUser(userId, roleId) {
      try {
        const userRoles = this.getUserRolesMapping();
        const oldRoleId = userRoles[userId];
  
        // Update user count for old role
        if (oldRoleId) {
          this.decrementRoleUserCount(oldRoleId);
        }
  
        // Assign new role
        userRoles[userId] = roleId;
        localStorage.setItem(this.USER_ROLES_KEY, JSON.stringify(userRoles));
  
        // Update user count for new role
        this.incrementRoleUserCount(roleId);
  
        return true;
      } catch (error) {
        console.error('Error assigning role:', error);
        return false;
      }
    }
  
    /**
     * Get user's role
     * @param {string} userId - User ID
     * @returns {string|null} Role ID or null
     */
    getUserRole(userId) {
      try {
        const userRoles = this.getUserRolesMapping();
        return userRoles[userId] || null;
      } catch (error) {
        console.error('Error getting user role:', error);
        return null;
      }
    }
  
    /**
     * Get user roles mapping
     * @returns {Object} User ID to Role ID mapping
     */
    getUserRolesMapping() {
      try {
        const mapping = localStorage.getItem(this.USER_ROLES_KEY);
        return mapping ? JSON.parse(mapping) : {};
      } catch (error) {
        console.error('Error getting user roles mapping:', error);
        return {};
      }
    }
  
    /**
     * Check if user has permission
     * @param {string} userId - User ID
     * @param {string} permissionId - Permission ID
     * @returns {boolean} Has permission
     */
    userHasPermission(userId, permissionId) {
      try {
        const roleId = this.getUserRole(userId);
        
        if (!roleId) {
          return false;
        }
  
        const role = this.getRoleById(roleId);
        
        if (!role) {
          return false;
        }
  
        // Super admin has all permissions
        if (role.permissions.includes('all')) {
          return true;
        }
  
        return role.permissions.includes(permissionId);
      } catch (error) {
        console.error('Error checking permission:', error);
        return false;
      }
    }
  
    /**
     * Get user's permissions
     * @param {string} userId - User ID
     * @returns {Array} Array of permission IDs
     */
    getUserPermissions(userId) {
      try {
        const roleId = this.getUserRole(userId);
        
        if (!roleId) {
          return [];
        }
  
        const role = this.getRoleById(roleId);
        
        if (!role) {
          return [];
        }
  
        // Super admin has all permissions
        if (role.permissions.includes('all')) {
          return this.getAllPermissions().map(p => p.id);
        }
  
        return role.permissions;
      } catch (error) {
        console.error('Error getting user permissions:', error);
        return [];
      }
    }
  
    /**
     * Increment role user count
     * @param {string} roleId - Role ID
     */
    incrementRoleUserCount(roleId) {
      try {
        const roles = this.getAllRoles();
        const roleIndex = roles.findIndex(r => r.id === roleId);
        
        if (roleIndex !== -1) {
          roles[roleIndex].userCount = (roles[roleIndex].userCount || 0) + 1;
          localStorage.setItem(this.ROLES_KEY, JSON.stringify(roles));
        }
      } catch (error) {
        console.error('Error incrementing user count:', error);
      }
    }
  
    /**
     * Decrement role user count
     * @param {string} roleId - Role ID
     */
    decrementRoleUserCount(roleId) {
      try {
        const roles = this.getAllRoles();
        const roleIndex = roles.findIndex(r => r.id === roleId);
        
        if (roleIndex !== -1 && roles[roleIndex].userCount > 0) {
          roles[roleIndex].userCount -= 1;
          localStorage.setItem(this.ROLES_KEY, JSON.stringify(roles));
        }
      } catch (error) {
        console.error('Error decrementing user count:', error);
      }
    }
  
    /**
     * Get role statistics
     * @returns {Object} Role statistics
     */
    getRoleStatistics() {
      try {
        const roles = this.getAllRoles();
        const userRoles = this.getUserRolesMapping();
        
        return {
          totalRoles: roles.length,
          systemRoles: roles.filter(r => r.isSystem).length,
          customRoles: roles.filter(r => !r.isSystem).length,
          totalUsers: Object.keys(userRoles).length,
          roleDistribution: roles.map(role => ({
            id: role.id,
            name: role.name,
            userCount: role.userCount,
            percentage: Object.keys(userRoles).length > 0 
              ? ((role.userCount / Object.keys(userRoles).length) * 100).toFixed(1)
              : 0
          }))
        };
      } catch (error) {
        console.error('Error getting statistics:', error);
        return null;
      }
    }
  }
  
  // Export singleton instance
  export const rolesPermissionsController = new RolesPermissionsController();